import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Crown, SpinnerGap, WarningCircle } from "@phosphor-icons/react";
import { paymentStatus } from "@/lib/api";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const pollRef = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let active = true;

    const poll = async () => {
      if (!active) return;
      if (pollRef.current >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      pollRef.current += 1;
      try {
        const res = await paymentStatus(sessionId);
        if (res.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(res.payment_status)) {
          setState("error");
          return;
        }
      } catch {
        // ignore transient
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      active = false;
    };
  }, [params]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0B0D17] px-5">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        data-testid="payment-success-page"
        className="w-full max-w-md rounded-3xl border border-white/10 bg-[#12141F] p-10 text-center"
      >
        {state === "checking" && (
          <>
            <SpinnerGap size={48} className="mx-auto animate-spin text-blue-400" />
            <h1 className="mt-6 font-display text-2xl font-bold text-white">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-slate-400">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/40">
              <CheckCircle size={40} weight="fill" className="text-emerald-400" />
            </div>
            <h1 className="mt-6 font-display text-2xl font-bold text-white">Premium Unlocked!</h1>
            <p className="mt-2 text-sm text-slate-400">
              Every premium listing's exclusive deal data is now available to you.
            </p>
            <div className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 px-4 py-1.5 text-sm font-semibold text-amber-300">
              <Crown size={15} weight="fill" /> Lifetime Premium Access
            </div>
            <button
              data-testid="back-to-directory-btn"
              onClick={() => navigate("/")}
              className="mt-8 w-full rounded-full bg-blue-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-500"
            >
              Back to Directory
            </button>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <WarningCircle size={48} weight="fill" className="mx-auto text-amber-400" />
            <h1 className="mt-6 font-display text-2xl font-bold text-white">
              {state === "timeout" ? "Still processing" : "Something went wrong"}
            </h1>
            <p className="mt-2 text-sm text-slate-400">
              {state === "timeout"
                ? "Your payment is taking longer than expected. It may still complete — check back shortly."
                : "We couldn't confirm your payment. No worries, you can try again."}
            </p>
            <button
              onClick={() => navigate("/")}
              className="mt-8 w-full rounded-full bg-white/10 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/15"
            >
              Back to Directory
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
